import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

/**
 * Children in this practice with an outstanding dose (KIN-1811 step 4).
 *
 * Read-only and derived on request, so it is a snapshot of what is true now rather than a
 * stored worklist — nothing here is claimed, assigned or marked done.
 *
 * Answers in the `ApiResponse` envelope, which the shared axios instance already unwraps;
 * reading `response.data.data` would unwrap it twice and render an empty screen.
 *
 * Deliberately not paginated by the caller: the backend says so, because the pagination
 * helper in that codebase pages in memory and pairing it with a limited query empties the
 * later pages. `limit` caps the cohort examined instead.
 */
export const useVaccineRecall = ({
  dueBy,
  includeUncertainHistory = false,
  /*
   * Defaults to true, matching the backend (KIN-1890). A contacted family stays on the
   * list, marked, rather than disappearing: hiding them understates the work outstanding,
   * and a practice manager looking at this list should see the whole picture. Pass false
   * to make the list workable a second time in one day.
   *
   * A SNOOZED course is hidden either way, because that is what snoozing means.
   */
  includeNotified = true,
  limit,
} = {}) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: [
      "vaccineRecall",
      tenantVal,
      dueBy,
      includeUncertainHistory,
      includeNotified,
      limit,
    ],
    enabled: !!tenantVal,
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}/immunisationschedule/recall`,
        {
          params: {
            ...(dueBy ? { dueBy } : {}),
            includeUncertainHistory,
            includeNotified,
            ...(limit ? { limit } : {}),
          },
        }
      );

      return response.data;
    },
    meta: { errorMessage: "Error fetching the recall list" },
  });
};
